import { useEditSpaceGroupModal } from "@circle-react/components/Modals/EditSpaceGroupModal";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { isIntendedOnNewTab } from "@circle-react/helpers/browserHelpers";
import { spaceGroupPaths } from "@circle-react/helpers/urlHelpers";
import { IconButton } from "@circleco/compass/components/IconButton";
import { Menu } from "@circleco/compass/components/Menu";
import { useNavigate } from "@tanstack/react-router";

import { t } from "@/i18n-js/instance";

import type { SpaceGroupWithSpaces } from "../../Sidebar";

export interface OptionsMenuProps {
  canManage: boolean;
  onToggle: (isOpen: boolean) => void;
  spaceGroup: SpaceGroupWithSpaces;
}

export const OptionsMenu = ({
  canManage,
  onToggle,
  spaceGroup,
}: OptionsMenuProps) => {
  const { isViewOnlyMasquerading } = usePunditUserContext();
  const navigate = useNavigate();
  const editSpaceGroupModal = useEditSpaceGroupModal();

  const seeSpacesLink = spaceGroupPaths.show({
    spaceGroupSlug: spaceGroup.slug,
  });
  const seeMembersLink = spaceGroupPaths.members({
    spaceGroupSlug: spaceGroup.slug,
  });

  const navigateTo =
    (path: string) => (event: React.MouseEvent<HTMLElement>) => {
      if (isIntendedOnNewTab(event)) {
        window.open(path, "_blank", "noopener,noreferrer");
        return;
      }

      void navigate({ href: path });
    };

  const options = [
    {
      label: t("space_groups.see_spaces"),
      onClick: navigateTo(seeSpacesLink),
    },
    ...(canManage
      ? [
          {
            label: t("space_groups.see_members"),
            onClick: navigateTo(seeMembersLink),
          },
        ]
      : []),
    ...(canManage && !isViewOnlyMasquerading
      ? [
          {
            label: t("space_groups.add_members"),
            onClick: () =>
              editSpaceGroupModal.show({ spaceGroup, tab: "Members" }),
          },
          {
            label: t("space_groups.edit_space_group"),
            onClick: () =>
              editSpaceGroupModal.show({ spaceGroup, tab: "Details" }),
          },
        ]
      : []),
  ];

  return (
    <Menu
      align="start"
      options={options}
      onOpenChange={onToggle}
      trigger={
        <IconButton
          aria-label={t("space_groups.view_space_group_options")}
          data-testid="spacegroup-options-button"
          icon="dot-menu"
          size="sm"
          variant="ghost"
          type="button"
        />
      }
    />
  );
};
